/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import { getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { CheckoutModule, ErrorLocation } from '@msdyn365-commerce/global-state';
import classname from 'classnames';
import { action, computed, observable, reaction, when } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { getUrlSync } from '@msdyn365-commerce/core';
import { AsynchronousPaymentParameters } from '@msdyn365-commerce/retail-proxy';
import { unlockCartAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import { getSalesTransactionCheckoutResultByTransactionIdAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/SalesOrdersDataActions.g';
import {
    AsynchronousPayment,
    FeatureState,
    PaymentLink,
    SalesTransactionCheckoutResult
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { focusOnCheckoutError } from '../../checkout-errors';
import { ICreateAsynchronousPaymentData } from './create-asynchronous-payment.data';
import { ICreateAsynchronousPaymentProps } from './create-asynchronous-payment.props.autogenerated';
import CreateAsynchronousPaymentButton from './components/create-asynchronous-payment.button';
import ErrorMessageComponent from './components/create-asynchronous-payment.error-message';
import ErrorTitleComponent from './components/create-asynchronous-payment.error-title';
import createAsynchronousPaymentAction, { CreateAsynchronousPaymentInput } from './actions/create-asynchronous-payment';
import AsynchronousPaymentSummary from './components/asynchronous-payment-summary';
import { EmptyActiveCart } from '../../common/empty-active-cart';

export interface ICreateAsynchronousPaymentModuleProps
    extends ICreateAsynchronousPaymentProps<ICreateAsynchronousPaymentData>,
        IModuleStateProps {}

export interface ICreateAsynchronousPayment {
    asynchronousPaymentContainer: INodeProps;
    button: React.ReactNode;
    summary?: React.ReactNode;
    asynchronousPayments?: AsynchronousPayment[] | null;
}

export interface ICreateAsynchronousPaymentError {
    error: INodeProps;
    title: React.ReactNode;
    message: React.ReactNode;
}

export interface ICreateAsynchronousPaymentViewState {
    isError: boolean;
    isShowSaveButton: boolean;
    isShowEditButton: boolean;
    isShowCancelButton: boolean;
}

export interface ICreateAsynchronousPaymentViewProps extends ICreateAsynchronousPaymentProps<{}> {
    createAsynchronousPayment: IModuleProps;
    errorMessageData: string | undefined;
    viewState: ICreateAsynchronousPaymentViewState;
    createAsynchronousPaymentContent: ICreateAsynchronousPayment;
    errorMessage?: ICreateAsynchronousPaymentError;
    createAsynchronousPaymentErrorRef?: React.RefObject<HTMLElement>;
    onClick(event: React.MouseEvent<HTMLButtonElement>): void;
    onCancelClick?(event: React.MouseEvent<HTMLButtonElement>): void;
}

/**
 *
 * CreateAsynchronousPayment component.
 * @extends {React.Component<ICreateAsynchronousPaymentProps<ICreateAsynchronousPaymentData>>}
 */
// @ts-expect-error
@withModuleState
@observer
class CreateAsynchronousPayment extends React.Component<ICreateAsynchronousPaymentModuleProps> {
    private readonly telemetryContent: ITelemetryContent;
    private readonly createAsynchronousPaymentErrorRef: React.RefObject<HTMLElement> = React.createRef();

    private pollingTimer?: number;
    private isPolling = false;
    private isUnmounted = false;

    private readonly pollIntervalMs: number;
    private readonly maxPollAttempts: number;
    private readonly shouldShowQrCode: boolean = false;
    private readonly asynchronousPaymentFeature: string = 'Dynamics.AX.Application.RetailAsyncPaymentsWithOmniChannelOrdersFeature';

    @observable private errorMessage?: string;
    @observable private paymentLinks: PaymentLink[] = [];
    @observable private asynchronousPayments?: AsynchronousPayment[] | null;

    constructor(props: ICreateAsynchronousPaymentModuleProps) {
        super(props);
        this.telemetryContent = getTelemetryObject(props.context.request.telemetryPageName ?? '', props.friendlyName, props.telemetry);

        const { pollIntervalMs, maxPollAttempts, shouldShowQrCode } = props.config ?? {};
        this.pollIntervalMs = typeof pollIntervalMs === 'number' && pollIntervalMs > 0 ? pollIntervalMs : 5000;
        this.maxPollAttempts = typeof maxPollAttempts === 'number' && maxPollAttempts > 0 ? maxPollAttempts : 12;
        this.shouldShowQrCode = shouldShowQrCode === true;
    }

    @computed get isDataReady(): boolean {
        return (this.props.data.checkout.result && this.props.data.checkout.status) === 'SUCCESS';
    }

    public async componentDidMount(): Promise<void> {
        when(
            () => this.isDataReady,
            async () => {
                await this.init();
            }
        );

        if (this.props.data.checkout.result?.shouldEnableCheckoutErrorDisplayMessaging) {
            reaction(
                () => this.props.data.checkout.result?.checkoutError,
                checkoutError => {
                    if (
                        checkoutError &&
                        checkoutError.errorLocation === ErrorLocation.CreateAsynchronousPayment &&
                        checkoutError.errorMessage
                    ) {
                        this.setError(checkoutError.errorMessage);
                    }
                }
            );

            reaction(
                () => this.props.data.checkout.result?.checkoutErrorFocus,
                checkoutErrorFocus => {
                    if (checkoutErrorFocus === CheckoutModule.CreateAsynchronousPayment && this.props.moduleState.hasError) {
                        void focusOnCheckoutError(this.createAsynchronousPaymentErrorRef, this.props.context.actionContext);
                    }
                }
            );
        }
    }

    public componentWillUnmount(): void {
        this.isUnmounted = true;
        this.stopPolling();
    }

    public shouldComponentUpdate(
        nextProps: ICreateAsynchronousPaymentModuleProps,
        nextState: ICreateAsynchronousPaymentViewState
    ): boolean {
        if (this.state === nextState && this.props === nextProps) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        const {
            moduleState: { hasError },
            config: { className }
        } = this.props;

        const viewProps: ICreateAsynchronousPaymentViewProps = {
            ...this.props,
            ...this.state,
            errorMessageData: this.errorMessage,
            createAsynchronousPaymentErrorRef: this.createAsynchronousPaymentErrorRef,
            viewState: {
                isError: hasError,
                isShowSaveButton: false,
                isShowEditButton: false,
                isShowCancelButton: this.paymentLinks.length > 0
            },
            createAsynchronousPayment: {
                moduleProps: this.props,
                className: classname('ms-create-asynchronous-payment', className)
            },
            createAsynchronousPaymentContent: this.getAsynchronousPaymentContent(),
            errorMessage: this.getErrorMessage(),
            onClick: this.onClick,
            onCancelClick: this.onCancelClick
        };
        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private readonly getAsynchronousPaymentContent = (): ICreateAsynchronousPayment => {
        const {
            moduleState: { isReady }
        } = this.props;

        // Disable when: module is doing work, a payment exists, or we're polling
        const isDisabled = isReady || this.paymentLinks.length > 0 || this.isPolling;
        const hasActivePaymentLink = this.paymentLinks.length > 0;
        const {
            buttonAriaLabel,
            buttonLabel,
            asynchronousPaymentLabel,
            payActionLabel,
            paymentLinkLabel,
            status,
            cancelBtnLabel,
            cancelBtnAriaLabel
        } = this.props.resources;
        return {
            asynchronousPaymentContainer: { className: 'ms-create-asynchronous-payment__container' },
            button: (
                <CreateAsynchronousPaymentButton
                    ariaLabel={buttonAriaLabel ?? ''}
                    disabled={isDisabled}
                    telemetryContent={this.telemetryContent}
                    buttonLabel={buttonLabel}
                    onClick={this.onClick}
                    className='ms-create-asynchronous-payment__button'
                />
            ),
            summary: (
                <AsynchronousPaymentSummary
                    containerClassName='ms-create-asynchronous-payment__summary'
                    paymentLinks={this.paymentLinks}
                    shouldShowQrCode={this.shouldShowQrCode}
                    cancelButton={
                        hasActivePaymentLink ? (
                            <CreateAsynchronousPaymentButton
                                ariaLabel={cancelBtnAriaLabel ?? ''}
                                disabled={false}
                                telemetryContent={this.telemetryContent}
                                buttonLabel={cancelBtnLabel}
                                onClick={this.onCancelClick}
                                className='ms-create-asynchronous-payment-cancel__button'
                            />
                        ) : (
                            undefined
                        )
                    }
                    resources={{
                        status: status,
                        paymentLinkLabel: paymentLinkLabel,
                        payActionLabel: payActionLabel,
                        asynchronousPaymentLabel: asynchronousPaymentLabel
                    }}
                />
            ),
            asynchronousPayments: this.asynchronousPayments
        };
    };

    @action
    private readonly init = async (): Promise<void> => {
        if (this.props.data.checkout.result?.isPaymentVerificationRedirection) {
            this.props.moduleState.setHasError(false);
            this.props.moduleState.onReady();
        }
        this.clearError();
        const checkoutCart = this.props.data.checkout.result?.checkoutCart.cart;
        if (checkoutCart?.AsynchronousPayments && checkoutCart.AsynchronousPayments.length > 0) {
            this.asynchronousPayments = checkoutCart.AsynchronousPayments;
            this.paymentLinks = this.mapAsynchronousPaymentsToLinks(checkoutCart.AsynchronousPayments);
            this.startPolling(checkoutCart.Id);
        }
        this.props.moduleState.init({
            isRequired: true,
            hasError: false,
            status: this.shouldEnableModule(this.props.data.featureState?.result) ? 'updating' : 'disabled'
        });
    };

    private readonly getErrorMessage = (): ICreateAsynchronousPaymentError => {
        const { errorMessageTitle } = this.props.resources;
        return {
            error: { tag: 'div', className: 'ms-checkout-asynchronous-payment__error', role: 'alert', 'aria-live': 'assertive' },
            title: <ErrorTitleComponent title={errorMessageTitle} />,
            message: <ErrorMessageComponent message={this.errorMessage} />
        };
    };

    private readonly onCancelClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
        this.props.telemetry.information('Cancel Asynchronous Payment onClick is called.');

        const checkoutCart = this.props.data.checkout.result?.checkoutCart.cart;

        if (!checkoutCart?.Id) {
            this.setError('Unable to cancel payment link: Cart ID not found.');
            return;
        }

        // Get the payment reference ID from the first active payment link
        const activePaymentLink = this.paymentLinks.find(link => link.PaymentLinkStatusValue === 0 || link.PaymentLinkStatusValue === 1);

        if (!activePaymentLink?.ReferenceId) {
            this.setError('Unable to cancel payment link: No active payment reference found.');
            return;
        }

        try {
            // Stop polling
            this.stopPolling();

            // Call the cancel payment link API
            const updatedCart = checkoutCart;

            if (updatedCart) {
                // Unlock the cart after cancellation (ignore errors if cart is already unlocked)
                try {
                    await unlockCartAsync({ callerContext: this.props.context.actionContext }, checkoutCart.Id, updatedCart.Version);
                } catch (unlockError) {
                    // Log the error but don't fail the cancellation process
                    this.props.telemetry.warning('Cart unlock failed, cart may already be unlocked');
                }

                // Clear local payment links and asynchronous payments
                this.paymentLinks = [];
                this.asynchronousPayments = null;

                // Refresh the cart to reflect the cancellation
                this.props.data.checkout.result?.checkoutCart.refreshCart({});

                this.props.telemetry.debug('Payment link cancelled successfully');
                this.clearError();
            } else {
                this.setError('Failed to cancel payment link: No response from server.');
            }
        } catch (e) {
            this.props.telemetry.exception(e as Error);
            this.setError('Failed to cancel asynchronous payment.');
        }
    };

    private readonly onClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
        this.props.telemetry.information('Create Asynchronous Payment onClick is called.');
        const checkoutCart = this.props.data.checkout.result?.checkoutCart.cart;
        const asyncParams: AsynchronousPaymentParameters = {
            TenderTypeId: '3',
            PaymentTypeValue: 1,
            PaymentAmount: checkoutCart?.TotalAmount ?? 0
        };

        try {
            const input = new CreateAsynchronousPaymentInput(asyncParams);
            const updatedCart = await createAsynchronousPaymentAction(input, this.props.context.actionContext).then(cart => {
                if (cart?.AsynchronousPayments?.length) {
                    this.props.data.checkout.result?.checkoutCart.refreshCart({});
                }
                return cart;
            });

            if (updatedCart?.AsynchronousPayments && updatedCart.AsynchronousPayments.length > 0 && updatedCart.Id) {
                this.asynchronousPayments = updatedCart.AsynchronousPayments;
                this.paymentLinks = this.mapAsynchronousPaymentsToLinks(updatedCart.AsynchronousPayments);
                this.startPolling(updatedCart.Id);
            } else {
                this.paymentLinks = [];
            }

            this.props.telemetry.debug('Updated cart after asynchronous payment creation');
            this.clearError();
        } catch (e) {
            this.setError('Failed to create asynchronous payment.');
        }
    };

    private mapAsynchronousPaymentsToLinks(payments?: AsynchronousPayment[] | null): PaymentLink[] {
        if (!payments || payments.length === 0) {
            return [];
        }

        const links: PaymentLink[] = [];

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        payments.forEach((payment: any) => {
            if (
                payment.ExtensibleAsynchronousPaymentTypeValue === 1 &&
                (payment.AsynchronousPaymentStatusValue === 0 || payment.AsynchronousPaymentStatusValue === 1)
            ) {
                links.push({
                    PaymentLinkId: payment.PaymentLinkId,
                    PaymentLinkUrl: payment.PaymentLinkUrl,
                    PaymentLinkStatusValue: payment.PaymentLinkStatusValue ?? payment.AsynchronousPaymentStatusValue,
                    Amount: payment.Amount,
                    CurrencyCode: payment.CurrencyCode,
                    ExpirationDateTime: payment.ExpirationDateTime,
                    TenderTypeId: payment.TenderTypeId,
                    ReferenceId: payment.ReferenceId,
                    ServiceAccountId: payment.ServiceAccountId
                } as PaymentLink);
            }
        });

        return links;
    }

    private startPolling(cartId: string): void {
        if (this.isPolling) {
            return;
        }
        this.isPolling = true;
        this.props.telemetry.information(`Polling started for cartId=${cartId}`);
        void this.pollSalesTransaction(cartId, 0, 0);
    }

    private stopPolling(): void {
        if (this.pollingTimer) {
            window.clearTimeout(this.pollingTimer);
            this.pollingTimer = undefined;
        }
        if (this.isPolling) {
            this.props.telemetry.information('Polling stopped.');
        }
        this.isPolling = false;
    }

    private readonly pollSalesTransaction = async (cartId: string, lastSentErrorLineNum: number, attempt: number): Promise<void> => {
        if (this.isUnmounted) {
            this.stopPolling();
            return;
        }

        if (attempt >= this.maxPollAttempts) {
            this.props.telemetry.warning(`Polling max attempts reached for cartId=${cartId}`);
            this.stopPolling();
            return;
        }

        try {
            const result: SalesTransactionCheckoutResult = await getSalesTransactionCheckoutResultByTransactionIdAsync(
                { callerContext: this.props.context.actionContext, bypassCache: 'none' },
                cartId,
                lastSentErrorLineNum
            );

            if (result.Status === 'Failed') {
                if (result?.CheckoutErrors && result.CheckoutErrors.length > 0) {
                    this.setError(result?.CheckoutErrors[0]?.ErrorLocalizedMessage || 'Asynchronous payment failed.');
                }
                this.stopPolling();
                return;
            }

            if (result.Status === 'Completed') {
                this.stopPolling();
                // Remove purchased items from the active cart
                try {
                    await EmptyActiveCart(this.props.context.actionContext);
                } catch (error) {
                    return;
                }

                // Redirect to the order confirmation page
                const orderConfirmationUrl = getUrlSync('orderConfirmation', this.props.context.actionContext) || '';
                if (!orderConfirmationUrl) {
                    return;
                }

                const separator = orderConfirmationUrl.includes('?') ? '&' : '?';
                const url = `${orderConfirmationUrl}${separator}transactionId=${cartId}`;
                window.location.assign(url);
                console.log(`Polling completed with status=${result.Status}`);
                return;
            }

            this.pollingTimer = window.setTimeout(() => {
                void this.pollSalesTransaction(cartId, lastSentErrorLineNum, attempt + 1);
            }, this.pollIntervalMs);
        } catch (e) {
            this.props.telemetry.exception(e as Error);
            this.setError('Failed while polling asynchronous payment status.');
            this.stopPolling();
        }
    };

    private readonly setError = (errorMessage: string): void => {
        this.props.telemetry.error(errorMessage);
        this.props.moduleState.setHasError(true);
        this.props.moduleState.onUpdating();
        this.errorMessage = errorMessage;
    };

    private readonly clearError = (): void => {
        this.props.moduleState.setHasError(false);
        this.errorMessage = undefined;
    };

    private readonly shouldEnableModule = (featureStates: FeatureState[] | undefined): boolean => {
        if (!featureStates || featureStates.length === 0) {
            return false;
        }
        const feature = featureStates.find(f => f?.Name === this.asynchronousPaymentFeature);
        return feature?.IsEnabled === true && this.props.data.checkout.result?.shouldEnableSinglePaymentAuthorizationCheckout === true;
    };
}

export default CreateAsynchronousPayment;
